/**
 * HANNE BLOCH cart drawer (2026-10-08, cart re-design wireframes v12, Vic's go).
 *
 * The markup is Liquid (snippets/hb-cart-lines, hb-cart-set, hb-cart-footer) and is re-rendered by the theme on
 * every cart change (cart-items-component morphs the header section). This script only acts:
 *   - lines: tap "Size: M" to open the sizes in the band under it, tap a size to swap the variant (quantity and
 *     properties kept); − and + (HB 2026-10-10, Vic: at 1 the minus removes the line, with Undo); Remove, then
 *     "Undo" for 5 seconds. Every removed line keeps its own Undo row for its own 5 seconds (HB 2026-10-10, Vic: a
 *     second Remove never cuts the first one short); the rows stand where the lines stood and close one by one, and
 *     Undo opens the line out again from the row's own place;
 *   - the set: on an add from a product page or quick add, a set that appears waits 1.5 s, then rises from the
 *     footer over 0.8 s with a fade; size choice; ADD TO MY ORDER (cart attribute hb_match=added:<handle>, the
 *     Klaviyo added-to-cart hook, the hb:match event): the set becomes her new line where it stands, right under
 *     the piece it belongs to, with "Added to your order" for 3 s (Vic, 2026-10-08: no fold, no jump);
 *   - the slot (HB 2026-10-10, Vic: never two suggestions at once): the gift module (snippets/hb-cart-tgift,
 *     assets/hb-tgift.js) has the slot until she reserves, adds or closes it; then one set at a time, the one for
 *     the piece she added most recently, the next after she has opened the cart twice with the same one on show;
 *     a set she closes with its x stays away until a new piece with a match is added, the gift she closes while her
 *     cart is under the amount comes back as ADD MY GIFT once it is over (a new offer), closed over the amount it
 *     folds to one line at the foot of the cart, "Our gift to you" with "Choose my gift", which brings the module
 *     itself back (HB 2026-10-10, round 29; until then the slide-up), so she is never locked out of it
 *     (sessionStorage). manageSlot() below;
 *   - the upsell → gift flow (HB 2026-10-10, Vic): when the set she accepts takes her cart over the gift's amount, her
 *     new line lands first ("Added to your order"), then the gift module unfolds into the slot 1.5 s later, over 0.8 s,
 *     the way the set itself rises after an add. In a held sheet the sheet grows with it in the same movement, by the
 *     room the module needs and never past the top bar's lower edge (riseGift, sheetCap below): the one change inside
 *     the open cart that is allowed to change its height. If she has left the cart meanwhile, the collection page's
 *     note offers the gift 5 s after the cart closed (assets/hb-tgift.js). HB 2026-10-10 (round 29): every entrance
 *     of a suggestion into a held sheet grows it that way, the gift's on any path (a +1 that takes the cart over the
 *     amount, "Choose my gift" on the folded line) and the set's too, and once a module stands at full height the
 *     lines are measured once more (settleSqueeze): a suggestion never cuts the piece above it;
 *   - the pins (HB 2026-10-12, Vic): with a belted top in the cart the pins module (snippets/hb-cart-pins,
 *     assets/hb-pins.js) is part of the gift field: above the mini bag's module and compact while that one has the
 *     slot, on its own otherwise, and the set waits behind it as it waits behind the gift (showPins);
 *   - the cart's height: the sheet or the full screen is chosen when the cart opens (one line or none a sheet, two
 *     or more the full screen). While it is open, changes made inside it (the set, sizes, quantity, Remove, Undo) keep it
 *     as it is, so it never jumps (Vic, 2026-10-08), the gift's entrance above being the one exception;
 *   - the trust row: re-applies "Arrives ..." (computed by snippets/delivery-estimate) after each re-render;
 *   - the empty cart (v13): with two or more pieces viewed (her browser's list, localStorage "viewedProducts"),
 *     up to three of them as lines under "Recently viewed" above the doors; else the doors only;
 *   - line order: Shopify lists the newest line first, and a size change is an add (the new size) plus a removal,
 *     so the line would jump to the top. The drawer keeps its own order of line keys (per tab, sessionStorage):
 *     new lines go on top, a changed or restored (Undo) line keeps its place.
 *   - the cart page (HB 2026-10-22, Vic: the drawers "are the guiding principle"): for consumers /cart renders this
 *     same drawer in the page (cart-drawer-component[data-hb-page], dialog.hb-cart--page, open from the start, never
 *     closed: pageMode() below). It is always the full screen's layout (never a sheet, so nothing holds or grows a
 *     height), and the quick add note's VIEW CART scrolls up to it instead of opening a drawer.
 * Every cart request asks for the drawer's section and hands it to the theme through one CartUpdateEvent, so the
 * drawer, the bag count and every other cart component update the way the theme does it.
 */
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const RISE_DELAY_MS = 1500;
const RISE_MS = 800;
const ARM_WINDOW_MS = 6000;
const UNDO_MS = 5000;
const UNDO_CLOSE_MS = 650; // the row fades, then closes (hb-cart.css)
const SHIFT_GUARD_MS = 1200; // taps on Remove and the quantity are ignored while the lines slide
const ORDER_STORE = 'hb-cart-order';
const ADDED_NOTE_MS = 3000;
// A size change, unhurried (Vic, 2026-10-08: "a bit more smooth and relaxed"): the ring glides to her size, the
// sizes stay a moment so she sees it taken, then fade; the line settles only after that, in place.
const SIZES_STAY_MS = 400; // after her last tap on a size (600 felt a touch slow, Vic)
const SIZES_FADE_MS = 250; // as .hb-line .hb-pills.is-closing in hb-cart.css
const SET_FOLD_MS = 600; // the set's line folds away before its piece becomes a cart line
const RECENT_MAX = 3;
const RECENT_MIN = 2; // Vic: the pieces she viewed only from two on; with one or none, the doors alone
const CHECK_SVG =
  '<svg width="11" height="11" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4.75 10.75l2.93 3.84c.1.14.16.21.22.23a.2.2 0 0 0 .18 0c.07-.02.12-.1.23-.23l6.93-8.85" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ADD_SOURCES_IGNORED = new Set(['hb-cart', 'cart-items-component', 'hb-gift-popup', 'hb-pins']);
// HB 2026-10-10: the gift's own state section (sections/hb-tgift-state) travels with the cart's requests when the gift
// feature is on the page, so assets/hb-tgift.js knows the new cart the moment the drawer does (no second fetch).
const GIFT_SECTION = 'hb-tgift-state';
const giftOnPage = () => !!document.getElementById('hb-tg-data');

const state = {
  armedAt: 0,
  seenSets: new Set(),
  riseTimer: null,
  undos: [], // the removed lines waiting for Undo: { key, variantId, quantity, properties, title, index, handle, until, timer, row }
  busy: false,
  guardUntil: 0,
  order: [], // line keys in the order the drawer shows them
  holdKeys: new Set(), // lines on their way back (Undo) or to their new size: their places are kept until they are rendered
  keepMode: null, // 'sheet' | 'full' while the open cart is changed from inside it
  justAdded: null, // the key of the set's piece, just added: the line gets its note once rendered
  landedAt: 0, // when the set's piece became a line (the gift module's entrance keeps its distance from it)
  noted: null, // { key, until }: the line carrying "Added to your order" (put back if the line is rendered anew)
  flip: null, // positions of the lines (and the set's row) just before the set's piece became a line
  removedHandles: new Set(), // pieces she removed on this page: not offered back under Recently viewed
  recent: null, // { promise, lines: [html] } for the empty cart
  declined: new Set(), // pieces she removed while the cart is open: the set does not offer them back meanwhile
  wasEmpty: null, // was the drawer showing the empty cart at the last render
  fillTimer: 0,
  swapping: null, // the line whose size is being saved
  swapNext: null, // a size she picked on that line meanwhile: sent as soon as the first is saved
  sizesTimer: 0, // the sizes row fading after her last tap on a size
  chain: Promise.resolve(), // Remove and Undo requests, sent one after the other
  pending: 0, // how many of them are on their way
  unpublished: null, // the last answer not yet handed to the drawer (a later request was already waiting)
  removingKeys: new Set(), // lines whose Remove is on its way
  qaPiece: null, // the piece in the quick add she just tapped: { name, img, at }
  slotTimer: 0, // the gift's "Your gift is reserved" moment before a set takes the slot
  compactTimer: 0, // the gift module's fade before it stands as its one line (closed over the amount)
  slotReveal: 0, // the next module's entrance, after the one before it has gone
  slotRevealFor: null,
  slotShut: false, // HB 2026-10-13: an x on the gift shuts the slot until the cart is closed and opened again
  hadGift: false, // was the gift module in the drawer at the last look
  giftLeftAt: 0, // when it went (its bag arriving): the set waits for the bag to float in
  pinsReveal: 0, // HB 2026-10-12: the pins module's entrance into the open cart, on its way
};

const drawer = () => document.querySelector('cart-drawer-component');
// HB 2026-10-22: the cart page, where the drawer stands open in the page (snippets/cart-drawer.liquid, hb_page_mode).
const pageMode = () => !!document.querySelector('cart-drawer-component[data-hb-page]');
const itemsComponent = () => document.querySelector('cart-drawer-component cart-items-component');
const sectionId = () => itemsComponent()?.dataset.sectionId;

/* ---------- the cart's height: sheet or full screen ---------- */

const dialogEl = () => document.querySelector('cart-drawer-component dialog.hb-cart');

function currentMode(dlg) {
  return dlg && dlg.classList.contains('hb-cart--full') ? 'full' : 'sheet';
}

// Liquid picks the mode from the line count on every re-render; while the cart is open and changed from inside,
// the mode it opened with stays.
function enforceMode() {
  const dlg = dialogEl();
  if (!dlg || !state.keepMode || pageMode()) return;
  if (!dlg.open) {
    state.keepMode = null;
    return;
  }
  const full = state.keepMode === 'full';
  if (dlg.classList.contains('hb-cart--full') !== full) dlg.classList.toggle('hb-cart--full', full);
  if (dlg.classList.contains('hb-cart--sheet') === full) dlg.classList.toggle('hb-cart--sheet', !full);
}

// As the cart opens it chooses again from what is in it (the same rule as snippets/cart-drawer.liquid: one
// top-level line or none a sheet, two and more the full screen), so a height kept earlier never outlives the visit.
function modeFromLines() {
  const dlg = dialogEl();
  if (!dlg || pageMode()) return; // the cart page keeps the full screen's layout
  const n = dlg.querySelectorAll('[data-hb-line]:not(.hb-line--nested):not(.hb-line--recent)').length;
  const full = n > 1;
  dlg.classList.toggle('hb-cart--full', full);
  dlg.classList.toggle('hb-cart--sheet', !full);
}

// Vic's rule, kept to the pixel: while the cart is open, changes made inside it never change its height. The
// first change made in an open sheet holds the sheet's height (a class and a variable on <html>, which the
// drawer's morph never touches); closing the cart lets go. The full screen needs no hold.
const phone = () => window.matchMedia('(max-width: 749px)').matches;

function hold() {
  const dlg = dialogEl();
  const root = document.documentElement;
  if (!dlg || !dlg.open || !phone() || root.classList.contains('hb-cart-held')) return;
  if (!dlg.classList.contains('hb-cart--sheet')) return;
  root.style.setProperty('--hb-cart-h', `${Math.round(dlg.getBoundingClientRect().height)}px`);
  root.classList.add('hb-cart-held');
}

function release() {
  const root = document.documentElement;
  clearTimeout(state.fillTimer);
  root.classList.remove('hb-cart-held', 'hb-cart-growing', 'hb-cart-emptied', 'hb-cart-filling');
  root.style.removeProperty('--hb-cart-h');
  state.declined.clear();
}

/* ---------- line order ---------- */

function loadOrder() {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(ORDER_STORE) || '[]');
    return Array.isArray(v) ? v.filter((k) => typeof k === 'string') : [];
  } catch (e) {
    return [];
  }
}

function saveOrder() {
  try {
    window.sessionStorage.setItem(ORDER_STORE, JSON.stringify(state.order));
  } catch (e) {
    /* no storage: the drawer simply follows Shopify's order */
  }
}

// After a size change or an Undo the line's key can change: the new key takes the old one's place.
function replaceKey(oldKey, newKey) {
  if (!oldKey || !newKey || oldKey === newKey) return;
  if (state.holdKeys.has(oldKey)) {
    state.holdKeys.delete(oldKey);
    state.holdKeys.add(newKey);
  }
  state.undos.forEach((u) => {
    if (u.key !== oldKey) return;
    u.key = newKey; // the row waiting for its line (Undo) follows the line's new key
    if (u.row) u.row.setAttribute('data-hb-undo', newKey);
  });
  const at = state.order.indexOf(oldKey);
  if (at === -1) return;
  if (state.order.includes(newKey)) state.order.splice(at, 1); // merged into a line that is already there
  else state.order[at] = newKey;
  saveOrder();
}

function applyOrder() {
  const form = document.querySelector('cart-drawer-component .hb-lines');
  if (!form) return;
  const lines = [...form.querySelectorAll(':scope > [data-hb-line]')];
  const keys = lines.map((l) => l.dataset.key);
  const present = new Set(keys);
  state.holdKeys.forEach((k) => present.has(k) && state.holdKeys.delete(k)); // back
  // The removed lines waiting for Undo, and the lines on their way back, keep their places.
  const held = new Set([...state.undos.map((u) => u.key), ...state.holdKeys].filter(Boolean));
  const fresh = keys.filter((k) => !state.order.includes(k));
  // HB 2026-10-10: Shopify renews a line's key when a discount's allocation changes (the gift bag arriving, say). The
  // line is the same, so it keeps its place: an unknown key takes over the place of a missing key with its variant
  // (seen: the set's piece jumped above the piece it belongs to as the bag floated in).
  const missing = state.order.filter((k) => !present.has(k) && !held.has(k));
  fresh.slice().forEach((k) => {
    const vid = k.split(':')[0];
    const old = missing.find((m) => m.split(':')[0] === vid);
    if (!old) return;
    state.order[state.order.indexOf(old)] = k;
    if (state.noted && state.noted.key === old) state.noted.key = k;
    missing.splice(missing.indexOf(old), 1);
    fresh.splice(fresh.indexOf(k), 1);
  });
  state.order = fresh.concat(state.order.filter((k) => present.has(k) || held.has(k)));
  state.undos.forEach((u) => {
    if (u.key && !state.order.includes(u.key)) state.order.splice(Math.min(u.index, state.order.length), 0, u.key);
  });
  saveOrder();
  const desired = state.order.filter((k) => present.has(k));
  if (!lines.length || desired.join('|') === keys.join('|')) return;
  const byKey = new Map(lines.map((l) => [l.dataset.key, l]));
  const ref = lines[lines.length - 1].nextSibling;
  desired.forEach((k) => form.insertBefore(byKey.get(k), ref));
}

/* ---------- cart requests ---------- */

async function cartPost(path, body) {
  const id = sectionId();
  const payload = Object.assign({}, body);
  if (id) {
    payload.sections = giftOnPage() ? `${id},${GIFT_SECTION}` : id;
    payload.sections_url = window.location.pathname;
  }
  const res = await fetch(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) {
    const err = new Error(data.description || data.message || `${path} failed`);
    err.data = data;
    throw err;
  }
  return data;
}

function countFromSections(data) {
  const html = data && data.sections && data.sections[sectionId()];
  if (html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const n = doc.querySelector('[ref="cartItemCount"]')?.textContent;
    if (n != null && n.trim() !== '') return parseInt(n, 10) || 0;
    return 0;
  }
  return typeof data?.item_count === 'number' ? data.item_count : undefined;
}

// Hands the new drawer markup to the theme: cart-items-component morphs, the bag count updates.
function publish(data, extra) {
  hold();
  const dlg = dialogEl();
  if (dlg && dlg.open && !state.keepMode) state.keepMode = currentMode(dlg);
  document.dispatchEvent(
    new CartUpdateEvent({}, 'hb-cart', Object.assign({ source: 'hb-cart', itemCount: countFromSections(data), sections: data.sections }, extra || {}))
  );
}

// Remove and Undo can be tapped as fast as she likes (Vic, 2026-10-08: "someone who spams removes is allowed to
// do so"). Their requests go to Shopify one after the other, and only the last answer re-renders the drawer: an
// earlier answer would still hold a line she has removed since, and bring it back for a moment.
function queued(send) {
  state.pending += 1;
  state.busy = true;
  const job = state.chain.then(send);
  state.chain = job.catch(() => {});
  return job
    .then((data) => {
      const last = state.pending === 1;
      state.unpublished = last ? null : data;
      return { data, last };
    })
    .finally(() => {
      state.pending -= 1;
      if (!state.pending) state.busy = false;
    });
}

// After a failed request: if an earlier answer was held back for it, the drawer gets that one now.
function publishHeldBack() {
  if (state.pending || !state.unpublished) return;
  const data = state.unpublished;
  state.unpublished = null;
  publish(data);
}

/* ---------- lines ---------- */

function lineOf(el) {
  return el.closest('[data-hb-line]');
}

function showLineMessage(line, text) {
  const msg = line && line.querySelector('[data-hb-msg]');
  if (!msg) return;
  msg.textContent = text;
  msg.hidden = false;
  setTimeout(() => {
    if (msg.isConnected) msg.hidden = true;
  }, 4000);
}

// HB 2026-10-21 (Vic: "remove should never drop below, so if the 6 swatches on a narrow phone need to take up more
// space than Remove allows, then Remove should disappear momentarily while selector is open"): the band never wraps
// now (hb-cart.css). While a row of sizes or swatches is open, Remove (and Undo's room beside it) steps aside when the
// row and Remove do not both fit on the line, and comes back once the row has closed. Measured, not guessed: the row's
// own width against the band's, so it holds for any number of swatches and any phone.
function fitBand(line) {
  const band = line && line.querySelector('.hb-line__band');
  if (!band) return;
  const open = band.querySelector('[data-hb-pills]:not([hidden])');
  if (!open) {
    band.classList.remove('is-crowded');
    return;
  }
  const acts = band.querySelector('.hb-line__acts');
  const was = band.classList.contains('is-crowded');
  band.classList.remove('is-crowded'); // measured with Remove in its place
  const gap = parseFloat(getComputedStyle(band).columnGap) || 0;
  const ml = parseFloat(getComputedStyle(open).marginLeft) || 0;
  const need = open.scrollWidth + ml + (acts ? gap + acts.offsetWidth : 0);
  const crowded = need > band.clientWidth + 0.5;
  band.classList.toggle('is-crowded', crowded);
  if (crowded !== was) band.offsetWidth; // the change lands before the row fades in, never after
}
function fitOpenBands() {
  document.querySelectorAll('[data-hb-line] .hb-line__band').forEach((band) => fitBand(lineOf(band)));
}
window.addEventListener('resize', () => requestAnimationFrame(fitOpenBands), { passive: true });

// The sizes fade out (and in, hb-cart.css) instead of snapping; the chevron turns with them.
function fadeSizes(pills) {
  if (!pills || pills.hidden) return;
  pills.classList.add('is-closing');
  clearTimeout(pills.hbClose);
  pills.hbClose = setTimeout(() => {
    pills.hidden = true;
    pills.classList.remove('is-closing');
    fitBand(lineOf(pills)); // Remove comes back once the row has gone
  }, SIZES_FADE_MS);
}

// HB 2026-10-20 (Vic, his iPhone: a bikini's Bottom opened while its Top row was still fading, both rows in the band
// for a quarter second, the new one pushed under Remove): a row in the same line makes way at once; rows in other
// lines still fade.
function hideSizesNow(pills) {
  if (!pills) return;
  clearTimeout(pills.hbClose);
  pills.classList.remove('is-closing');
  pills.hidden = true;
  fitBand(lineOf(pills));
}

function closeAllSizes(except) {
  clearTimeout(state.sizesTimer);
  const exLine = except ? lineOf(except) : null;
  document.querySelectorAll('[data-hb-line] .hb-opt[aria-expanded="true"]').forEach((btn) => {
    if (btn === except) return;
    btn.setAttribute('aria-expanded', 'false');
    const line = lineOf(btn);
    const pills = line && line.querySelector(`[data-hb-pills="${btn.dataset.hbOpt}"]`);
    if (exLine && line === exLine) hideSizesNow(pills);
    else fadeSizes(pills);
  });
}

function toggleSizes(btn) {
  const line = lineOf(btn);
  if (!line) return;
  const open = btn.getAttribute('aria-expanded') === 'true';
  closeAllSizes(open ? null : btn);
  const pills = line.querySelector(`[data-hb-pills="${btn.dataset.hbOpt}"]`);
  if (!pills) return;
  btn.setAttribute('aria-expanded', open ? 'false' : 'true');
  if (open) {
    fadeSizes(pills);
  } else {
    clearTimeout(pills.hbClose); // opened again while fading: it simply stays
    pills.classList.remove('is-closing');
    pills.hidden = false;
    fitBand(line); // HB 2026-10-21: Remove steps aside when the row needs its room
  }
}

// Resolves once the line's sizes have faded (or after max ms, if she keeps them open).
function sizesClosed(line, max = 1800) {
  const start = performance.now();
  return new Promise((resolve) => {
    const check = () => {
      const open = line.isConnected && line.querySelector('[data-hb-pills]:not([hidden])');
      if (!open || performance.now() - start > max) resolve();
      else setTimeout(check, 40);
    };
    check();
  });
}

// The saved line keeps its element: given the new key's id before the drawer re-renders, the morph updates it in
// place (picture untouched) instead of taking it out and putting a new one in.
function keepLineElement(line, key) {
  if (!line || !line.isConnected || !key || line.dataset.key === key) return;
  const id = `hb-line-${key.replace(/:/g, '-')}`;
  if (document.getElementById(id)) return; // merged into a line already there: that one stays
  line.id = id;
  line.dataset.key = key;
}

// HB 2026-10-10: a line's properties as sent to the cart. Liquid's json filter writes a line's properties as a
// list of pairs ([["_free_gift","Yes"],["_hb_gift","threshold"]]), not an object; read that way, a size swap or an
// Undo on a line with properties used to send none (the gift bag lost its _hb_gift mark when its colour changed).
function lineProperties(line) {
  let properties = {};
  try {
    properties = JSON.parse(line.dataset.properties || '{}') || {};
  } catch (e) {
    properties = {};
  }
  if (Array.isArray(properties)) {
    properties = properties.reduce((o, pair) => {
      if (Array.isArray(pair) && pair.length >= 2 && pair[0] != null) o[String(pair[0])] = pair[1];
      return o;
    }, {});
  }
  return properties && typeof properties === 'object' ? properties : {};
}

async function swapVariant(pill) {
  const line = lineOf(pill);
  if (!line) return;
  // A size picked while her previous size is still saving is kept and sent next, never dropped (Vic: no extra tap).
  const queuing = state.busy && state.swapping === line;
  if (state.busy && !queuing) return;
  const newId = Number(pill.dataset.variantId);
  const shownId = Number(line.dataset.hbShown || line.dataset.variantId);
  if (!newId || newId === shownId) {
    closeAllSizes();
    return;
  }
  hold();

  // At once: the ring moves to her size and the line reads it (Vic: no lag); the row fades a moment later.
  const group = pill.closest('[data-hb-pills]');
  const btn = group && line.querySelector(`.hb-opt[data-hb-opt="${group.dataset.hbPills}"]`);
  const label = btn && btn.querySelector('.hb-opt__tx');
  const before = queuing ? null : { sel: group && group.querySelector('.hb-pill.is-sel'), text: label && label.textContent };
  if (group) group.querySelectorAll('.hb-pill').forEach((p) => p.classList.toggle('is-sel', p === pill));
  if (label) label.textContent = label.textContent.replace(/:\s.*$/, `: ${pill.textContent.trim()}`);
  line.dataset.hbShown = String(newId);
  line.classList.add('is-saving');
  clearTimeout(state.sizesTimer);
  state.sizesTimer = setTimeout(() => closeAllSizes(), SIZES_STAY_MS);
  if (queuing) {
    state.swapNext = newId;
    return;
  }

  const properties = lineProperties(line);
  const plain = Object.keys(properties).length === 0;
  const quantity = Number(line.dataset.quantity) || 1;
  const firstKey = line.dataset.key;
  state.busy = true;
  state.swapping = line;
  state.swapNext = null;

  let fromKey = firstKey;
  let to = newId;
  let data = null;
  let items = null; // the cart after the last request (null before the first)
  try {
    for (;;) {
      const already = items
        ? items.some((i) => Number(i.variant_id) === to && i.key !== fromKey)
        : !!document.querySelector(`cart-drawer-component [data-hb-line][data-variant-id="${to}"]`);
      let newKey;
      if (plain && !already) {
        // One request: the old line to 0 and the new size in, together (a refusal leaves the cart as it was).
        data = await cartPost('cart/update.js', { updates: { [fromKey]: 0, [to]: quantity } });
        const item = (data.items || []).find(
          (i) => Number(i.variant_id) === to && (!i.properties || Object.keys(i.properties).length === 0)
        );
        newKey = item && item.key;
      } else if (properties._hb_gift === 'threshold' || properties._hb_gift === 'pins') {
        // HB 2026-10-10 (Vic, desktop test): the gift bag's colour. Her bag goes out first, then the new colour comes
        // in with the same properties. Added first, the automatic discount moves between the two bags and Shopify
        // gives both lines new keys, so the old key then removes nothing and she is left with two bags, one at full
        // price (seen in the test). If the new colour is refused, her bag comes back as it was.
        // HB 2026-10-12: the pins with a belted top (_hb_gift = pins, Gold or Silver) swap the same way.
        const oldId = Number(String(fromKey).split(':')[0]) || Number(line.dataset.variantId);
        await cartPost('cart/change.js', { id: fromKey, quantity: 0 });
        try {
          data = await cartPost('cart/add.js', { items: [{ id: to, quantity, properties }] });
        } catch (err) {
          let back = null;
          try {
            back = await cartPost('cart/add.js', { items: [{ id: oldId, quantity, properties }] });
          } catch (e2) {
            back = null;
          }
          if (back) {
            const item = (back.items || []).find((i) => Number(i.variant_id) === oldId);
            if (item && item.key) {
              replaceKey(fromKey, item.key);
              fromKey = item.key;
              keepLineElement(line, fromKey);
            }
            data = back;
            items = back.items || [];
          }
          err.hbGiftRestored = true;
          throw err;
        }
        const item = (data.items || []).find((i) => Number(i.variant_id) === to && i.properties && i.properties._hb_gift === properties._hb_gift);
        newKey = item && item.key;
      } else {
        // With properties (or her new size already in the cart): the new size first, then the old line out.
        const added = await cartPost('cart/add.js', { items: [{ id: to, quantity, properties }] });
        data = await cartPost('cart/change.js', { id: fromKey, quantity: 0 });
        newKey = added && added.items && added.items[0] && added.items[0].key;
      }
      items = data.items || [];
      replaceKey(fromKey, newKey);
      if (newKey) fromKey = newKey;
      // The line carries its new key at once: a re-check of the order meanwhile (her next tap changes the label)
      // then finds it where it was, instead of taking it for a new line and moving it to the top.
      keepLineElement(line, fromKey);
      let next = state.swapNext;
      state.swapNext = null;
      if (!next || next === to) {
        // The drawer re-renders only once her sizes have faded; a size picked meanwhile is still sent.
        await sizesClosed(line);
        next = state.swapNext;
        state.swapNext = null;
        if (!next || next === to) break;
      }
      to = next;
    }
    if (fromKey !== firstKey) state.holdKeys.add(fromKey);
    keepLineElement(line, fromKey);
    publish(data);
  } catch (err) {
    if (err && err.hbGiftRestored && data) {
      // The gift's new colour was refused and her bag is back (under a new key): the cart as it is, and the word.
      if (fromKey !== firstKey) state.holdKeys.add(fromKey);
      keepLineElement(line, fromKey);
      line.classList.remove('is-saving');
      delete line.dataset.hbShown;
      if (group && before) group.querySelectorAll('.hb-pill').forEach((p) => p.classList.toggle('is-sel', p === before.sel));
      if (label && before && before.text) label.textContent = before.text;
      publish(data);
      showLineMessage(line, 'This color is not available right now.');
    } else if (data) {
      // The first size went through, a later one did not: show the cart as it is.
      if (fromKey !== firstKey) state.holdKeys.add(fromKey);
      keepLineElement(line, fromKey);
      publish(data);
    } else {
      line.classList.remove('is-saving');
      delete line.dataset.hbShown;
      if (group && before) group.querySelectorAll('.hb-pill').forEach((p) => p.classList.toggle('is-sel', p === before.sel));
      if (label && before && before.text) label.textContent = before.text;
      // HB 2026-10-10: the gift bag's colour swatches (data-hb-pills="tg") go through here too; the message says colour.
      showLineMessage(line, group && (group.dataset.hbPills === 'tg' || group.dataset.hbPills === 'pins') ? 'This color is not available right now.' : 'This size is not available right now.');
    }
  } finally {
    state.busy = false;
    state.swapping = null;
    state.swapNext = null;
  }
}

async function changeQuantity(btn) {
  const line = lineOf(btn);
  if (!line || state.busy) return;
  const delta = Number(btn.dataset.hbQty);
  const current = Number(line.dataset.quantity) || 1;
  const next = current + delta;
  if (next < 1) {
    // HB 2026-10-10 (Vic): the minus at 1 takes the line out, the same way as Remove (the line folds, Undo offered).
    removeLine(btn);
    return;
  }
  hold();
  state.busy = true;
  line.classList.add('is-busy');
  try {
    const data = await cartPost('cart/change.js', { id: line.dataset.key, quantity: next });
    const after = (data.items || []).find((i) => i.key === line.dataset.key);
    publish(data);
    if (after && after.quantity < next) {
      // Shopify capped the quantity (stock): say so quietly on the re-rendered line.
      requestAnimationFrame(() => {
        const fresh = document.querySelector(`[data-hb-line][data-key="${CSS.escape(line.dataset.key)}"]`);
        showLineMessage(fresh, 'No more available in this size.');
      });
    }
  } catch (err) {
    line.classList.remove('is-busy');
    showLineMessage(line, 'No more available in this size.');
  } finally {
    state.busy = false;
  }
}

function lineTitle(line) {
  const t = line.querySelector('.hb-line__title');
  return t ? t.textContent.replace(/\s+/g, ' ').trim() : 'The piece';
}

async function removeLine(btn) {
  const line = lineOf(btn);
  if (!line || state.removingKeys.has(line.dataset.key)) return;
  // A size, quantity or the set being saved: wait for it. Other Removes and Undo: queue behind them.
  if (state.busy && !state.pending) return;
  const properties = lineProperties(line);
  hold();
  const key = line.dataset.key;
  state.removingKeys.add(key);
  // Her place among the lines that stay (lines on their way out do not count).
  const lines = [...document.querySelectorAll('cart-drawer-component [data-hb-line]')].filter(
    (l) => l === line || !state.removingKeys.has(l.dataset.key)
  );
  if (line.dataset.handle) {
    state.removedHandles.add(line.dataset.handle);
    state.declined.add(line.dataset.handle);
  }
  const removed = {
    key: line.dataset.key,
    variantId: Number(line.dataset.variantId),
    quantity: Number(line.dataset.quantity) || 1,
    properties,
    title: lineTitle(line),
    index: lines.indexOf(line),
    handle: line.dataset.handle || '',
  };
  // Her last piece: the viewed pieces for the empty cart are fetched now (without this one), and the cart's foot
  // (shipping, trust row, CHECK OUT) fades while the line folds, so the empty cart can come in as one movement.
  const others = [...document.querySelectorAll('cart-drawer-component .hb-lines > [data-hb-line]')].filter(
    (l) => l !== line && !state.removingKeys.has(l.dataset.key)
  );
  const summary = others.length ? null : document.querySelector('cart-drawer-component .cart-drawer__summary');
  if (!others.length) {
    loadRecent();
    if (summary) summary.classList.add('hb-leaving');
  }
  // The set offered for this piece folds away with it (it would go anyway when the cart answers).
  const set = setEl();
  const setGoes = !!(set && line.dataset.handle && set.dataset.triggerHandle === line.dataset.handle && !set.classList.contains('is-declined'));
  if (setGoes) {
    set.style.maxHeight = `${set.offsetHeight}px`;
    set.style.transition = 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease'; // done before the cart answers
    void set.offsetHeight;
    set.classList.add('is-folding');
  }
  // In one movement (Vic, 2026-10-08): the line folds away while the Undo row opens in its place. A row already
  // standing for an earlier Remove keeps its own time (Vic, 2026-10-10).
  const row = startUndo(removed);
  line.parentNode.insertBefore(row, line);
  row.style.maxHeight = '0px';
  row.style.paddingTop = '0px';
  row.style.paddingBottom = '0px';
  row.style.opacity = '0';
  line.style.maxHeight = `${line.offsetHeight}px`;
  line.style.overflow = 'hidden';
  void line.offsetHeight;
  line.style.transition = 'max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), padding 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease, border-color 0.18s ease';
  line.style.maxHeight = '0px';
  line.style.paddingTop = '0px';
  line.style.paddingBottom = '0px';
  line.style.opacity = '0';
  line.style.borderBottomColor = 'transparent';
  setTimeout(() => {
    if (line.isConnected) line.style.borderBottomWidth = '0px';
  }, 330);
  row.style.transition = 'max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), padding 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease 0.12s';
  row.style.maxHeight = '64px';
  row.style.paddingTop = '';
  row.style.paddingBottom = '';
  row.style.opacity = '';
  setTimeout(() => {
    row.style.transition = '';
    row.style.maxHeight = '';
  }, 460);
  try {
    const { data, last } = await queued(() => cartPost('cart/change.js', { id: key, quantity: 0 }));
    if (last) publish(data);
  } catch (err) {
    const u = undoFor(key);
    if (u) stopUndo(u, true);
    if (summary) summary.classList.remove('hb-leaving');
    if (setGoes && set.isConnected) {
      set.classList.remove('is-folding');
      set.style.maxHeight = '';
      set.style.transition = '';
    }
    ['maxHeight', 'overflow', 'transition', 'paddingTop', 'paddingBottom', 'opacity', 'borderBottomColor', 'borderBottomWidth'].forEach((k) => {
      line.style[k] = '';
    });
    showLineMessage(line, 'Something went wrong. Please try again.');
    publishHeldBack();
  } finally {
    state.removingKeys.delete(key);
  }
}

/* ---------- Undo ---------- */

// HB 2026-10-10 (Vic): every removed line has its own Undo row for its own 5 seconds. A second Remove opens a second
// row and leaves the first one standing; each closes when its own time is up (or at once when she taps its Undo).
let undoSeq = 0;

function buildUndoRow(u) {
  const row = document.createElement('div');
  row.className = 'hb-undo';
  row.id = `hb-undo-${(undoSeq += 1)}`; // an id of its own, so the drawer's morph never rewrites a line into it
  row.setAttribute('data-hb-undo', u.key || '');
  row.setAttribute('role', 'status');
  const text = document.createElement('span');
  text.textContent = `${u.title} removed.`;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'hb-link hb-undo__btn';
  btn.setAttribute('data-hb-undo-btn', '');
  btn.textContent = 'Undo';
  row.append(text, btn);
  return row;
}

function undoFor(key) {
  return state.undos.find((u) => u.key === key) || null;
}

function startUndo(removed) {
  const earlier = undoFor(removed.key);
  if (earlier) stopUndo(earlier, true); // the same line again (Undo, then Remove): one row for it
  const u = Object.assign({}, removed, { until: Date.now() + UNDO_MS });
  u.timer = setTimeout(() => stopUndo(u, false), UNDO_MS);
  // One row element for the whole 5 s: the drawer's re-render takes it out, placeUndo() puts the same one back.
  u.row = buildUndoRow(u);
  state.undos.push(u);
  return u.row;
}

function stopUndo(u, immediately) {
  if (!u || !state.undos.includes(u)) return;
  clearTimeout(u.timer);
  state.undos = state.undos.filter((x) => x !== u);
  if (u.key && !state.holdKeys.has(u.key) && !document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(u.key)}"]`)) {
    state.order = state.order.filter((k) => k !== u.key);
    saveOrder();
  }
  const row = u.row;
  u.row = null;
  if (!row || !row.isConnected) return;
  if (immediately) {
    row.remove();
    return;
  }
  // A tap aimed at Undo as it closes must not land on the Remove of the line that slides into its place.
  state.guardUntil = performance.now() + SHIFT_GUARD_MS;
  row.style.maxHeight = `${row.offsetHeight}px`;
  void row.offsetHeight;
  row.classList.add('is-gone');
  setTimeout(() => row.remove(), UNDO_CLOSE_MS);
}

// Keeps every Undo row where its line stood, through every re-render: in the drawer's own order of line keys (a
// removed line keeps its key in the order until its Undo has passed, applyOrder), a row right above its line while
// that line is still folding away. In the emptied cart the rows stand at the top, in that same order.
function placeUndo() {
  const live = state.undos.filter((u) => u.row && (u.restoring || Date.now() <= u.until));
  if (!live.length) return;
  const root = drawer();
  if (!root) return;
  const rows = new Map(live.map((u) => [u.key, u.row]));
  const form = root.querySelector('.hb-lines');
  if (form) {
    const lines = [...form.querySelectorAll(':scope > [data-hb-line]')];
    const byKey = new Map(lines.map((l) => [l.dataset.key, l]));
    const seq = [];
    state.order.forEach((k) => {
      if (rows.has(k)) seq.push(rows.get(k));
      if (byKey.has(k)) seq.push(byKey.get(k));
    });
    lines.forEach((l) => !seq.includes(l) && seq.push(l));
    live.forEach((u) => !seq.includes(u.row) && seq.unshift(u.row));
    const placed = [...form.children].filter((c) => seq.includes(c));
    if (placed.length === seq.length && placed.every((c, i) => c === seq[i])) return; // all in place
    let ref = null;
    for (let i = form.children.length - 1; i >= 0; i -= 1) {
      if (seq.includes(form.children[i])) {
        ref = form.children[i].nextSibling;
        break;
      }
    }
    seq.forEach((el) => form.insertBefore(el, ref));
    return;
  }
  const items = root.querySelector('.cart-drawer__items');
  if (!items) return;
  const seq = state.order.map((k) => rows.get(k)).filter(Boolean);
  live.forEach((u) => !seq.includes(u.row) && seq.unshift(u.row));
  seq.forEach((row, i) => {
    if (items.children[i] !== row) items.insertBefore(row, items.children[i] || null);
  });
}

// Undo (HB 2026-10-10, Vic: "it should just expand back out to where it just shrank from"): the row stays where it is,
// its Undo taken, while the line is fetched back; when the drawer shows the line again it opens out from the row's
// height in the same place (settleRestored) and the row goes.
async function undo(btn) {
  const row = btn && btn.closest('[data-hb-undo]');
  const u = row ? undoFor(row.getAttribute('data-hb-undo')) : null;
  if (!u || u.restoring || (state.busy && !state.pending)) return;
  hold();
  if (u.handle) state.declined.delete(u.handle);
  // Hold its place in the order until it is back (stopUndo and applyOrder drop keys that are not in the cart).
  const at = Math.min(u.index, state.order.length);
  state.holdKeys.add(u.key);
  u.restoring = true;
  clearTimeout(u.timer);
  if (u.row) u.row.classList.add('is-restoring');
  if (u.key && !state.order.includes(u.key)) {
    state.order.splice(at, 0, u.key);
    saveOrder();
  }
  try {
    const item = { id: u.variantId, quantity: u.quantity };
    if (u.properties && Object.keys(u.properties).length) item.properties = u.properties;
    // cart/add.js answers with the added item only, so the drawer's markup comes from the sections it returns.
    const { data, last } = await queued(() => cartPost('cart/add.js', { items: [item] }));
    replaceKey(u.key, data && data.items && data.items[0] && data.items[0].key);
    if (last) publish(data);
  } catch (err) {
    state.holdKeys.delete(u.key);
    console.error('[HB cart] undo failed', err);
    stopUndo(u, false);
    publishHeldBack();
  }
}

// The line is back: it opens out of its row's place (the row's height to its own, 0.4 s) and the row goes. A line
// that merged into one already in the cart (its quantity grew) is left as rendered.
function settleRestored() {
  state.undos.filter((u) => u.restoring).forEach((u) => {
    const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(u.key)}"]`);
    if (!line) return;
    const from = u.row && u.row.isConnected ? u.row.offsetHeight : 0;
    const merged = (Number(line.dataset.quantity) || 1) !== u.quantity;
    stopUndo(u, true);
    if (!from || merged) return;
    const to = line.offsetHeight;
    line.style.transition = 'none';
    line.style.overflow = 'hidden';
    line.style.maxHeight = `${from}px`;
    line.style.opacity = '0.35';
    void line.offsetHeight;
    line.style.transition = 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease 0.1s';
    line.style.maxHeight = `${to}px`;
    line.style.opacity = '';
    setTimeout(() => {
      ['maxHeight', 'overflow', 'transition', 'opacity'].forEach((k) => {
        line.style[k] = '';
      });
    }, 460);
  });
}

/* ---------- the set, and the slot it shares with the gift ---------- */

// HB 2026-10-10 (Vic): one suggestion in the cart at a time. snippets/hb-cart-set now renders a module for every
// piece with a match (newest piece first), snippets/hb-cart-tgift the gift module; this manager shows exactly one:
//   - the gift first, in every cart with a piece in it, until she reserves (then a set may take the slot, after
//     "Your gift is reserved" has been read), adds (the module goes, the bag floats in, then a set rises) or
//     closes it with the small x in its line (it stays away for this visit; a reserved bag still arrives by itself);
//   - then one set: the swimsuit's top first (a pair "... Swimsuit > ... Top", data-rank 0), else the set for the
//     piece she added most recently.
// HB 2026-10-13 (round 40, fix 5.85; Vic: "a cart should never spam-suggest ... At most suggest 1 item for upsell, and
// then 1 item for gifting. That should be absolute maximum in the same cart"):
//   - an x on a set closes every pair on offer at that moment for the visit (data-pair, style to style: "bandeau silk
//     swimsuit>bandeau top"); another color of the same pair never brings it back, a different pair (another swimsuit
//     style with its top) shows once, as a new offer;
//   - an x on the gift shuts the slot until she has closed and opened the cart again: nothing rises in its place;
//   - no rotation any more: the one set on show stays until she takes it, closes it or its piece leaves the bag.
// A module leaves with a short fade (is-leaving), the next rises in (is-entering); hidden modules carry is-yield.
const SLOT_SWAP_MS = 350;
const RESERVED_HOLD_MS = 1100; // "Your gift is reserved" is read before a set takes the slot (the cart's own answer adds about a second)
const GIFT_GONE_WAIT_MS = 900; // after the gift module left for its bag, the set waits for the bag to float in
const TG_DISMISS_KEY = 'hb-tg-dismissed';
const SET_DISMISS_KEY = 'hb-set-dismissed'; // HB 2026-10-13: { pairs: [closed pairs, style>style] } (older shapes are ignored)

function setEls() {
  return [...document.querySelectorAll('cart-drawer-component [data-hb-set]')];
}

// The set on show (the one the slot gave the turn to), for Remove's fold and the clip.
function setEl() {
  return setEls().find((set) => !set.classList.contains('is-yield')) || null;
}

function giftEl() {
  return document.querySelector('cart-drawer-component [data-hb-tg-mod]');
}

// HB 2026-10-12 (Vic: "When a Belted Top is in cart, then it should show 2 pins in the 'gift' field for the customer to
// add"): the pins module (snippets/hb-cart-pins, assets/hb-pins.js) belongs to the gift field. With the mini bag on
// offer the two stand together, the pins first and compact (is-stacked); without it the pins have the gift's turn; the
// set waits behind both, as it waits behind the gift. Closed with its x it stays away until another belted top is added.
const PINS_DISMISS_KEY = 'hb-pins-dismissed'; // how many belted tops were in her cart when she closed the pins

function pinsEl() {
  return document.querySelector('cart-drawer-component [data-hb-pins-mod]');
}

function pinsTops(pins) {
  const inner = pins && pins.querySelector('[data-hb-pins-in]');
  return (inner && Number(inner.dataset.tops)) || 0;
}

function pinsDismissed(pins) {
  let v = null;
  try {
    v = sessionStorage.getItem(PINS_DISMISS_KEY);
  } catch (e) {
    return false;
  }
  if (!v) return false;
  if (pinsTops(pins) > (Number(v) || 0)) {
    // another belted top since she closed it: a new offer
    try {
      sessionStorage.removeItem(PINS_DISMISS_KEY);
    } catch (e) {
      /* nothing to forget */
    }
    return false;
  }
  return true;
}

function dismissPins() {
  const pins = pinsEl();
  if (!pins) return;
  try {
    sessionStorage.setItem(PINS_DISMISS_KEY, String(pinsTops(pins) || 1));
  } catch (e) {
    /* for this page, then */
  }
  hold(); // the swap in the slot never changes the open sheet's height
  manageSlot(false);
}

// The pins module on show or not, and stacked over the mini bag or on its own. Rendered by Liquid as it should look
// when the cart opens; a module that is new while the cart is open is hidden at once (this runs in a microtask after
// the drawer's morph, before a frame is drawn) and unfolds 1.5 s after her new line, as the set and the gift do.
function showPins(pins, on, stacked) {
  if (!pins) return;
  if (pins.classList.contains('is-stacked') !== stacked) pins.classList.toggle('is-stacked', stacked);
  const open = !!(dialogEl() && dialogEl().open);
  const fresh = !pins.hbKnown;
  pins.hbKnown = true;
  if (!on) {
    clearTimeout(state.pinsReveal);
    state.pinsReveal = 0;
    if (pins.classList.contains('is-yield') || pins.classList.contains('is-leaving')) return;
    if (!open || fresh) {
      pins.classList.add('is-yield');
      return;
    }
    pins.classList.add('is-leaving');
    setTimeout(() => {
      if (!pins.isConnected) return;
      pins.classList.add('is-yield');
      pins.classList.remove('is-leaving');
    }, SLOT_SWAP_MS);
    return;
  }
  if (!open) {
    clearTimeout(state.pinsReveal);
    state.pinsReveal = 0;
    pins.classList.remove('is-yield', 'is-leaving', 'is-waiting', 'is-rising', 'is-measuring');
    return;
  }
  if (fresh) pins.classList.add('is-yield');
  if (!pins.classList.contains('is-yield') && !pins.classList.contains('is-leaving')) return; // on show already
  if (state.pinsReveal) return; // its entrance is on its way
  pins.classList.remove('is-leaving');
  const since = state.justAdded ? 0 : state.landedAt ? performance.now() - state.landedAt : Infinity;
  const sinceAdd = state.armedAt ? performance.now() - state.armedAt : Infinity;
  const wait = Math.max(fresh ? 250 : 0, RISE_DELAY_MS - Math.min(since, sinceAdd));
  state.pinsReveal = setTimeout(() => {
    state.pinsReveal = 0;
    riseModule(pins);
  }, wait);
}

function readStore(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

function writeStore(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* private mode: the choice lives for this page only */
  }
}

// HB 2026-10-10 (Vic, the upsell → gift flow): the x on the gift remembers the offer she closed. Closed under the
// amount ("Reserve my gift"), the module comes back as ADD MY GIFT once her cart is over it, a new offer; closed over
// the amount, it stays away for this visit. (A value of '1' is from before this rule: away for the visit.)
function giftDismissed(mode) {
  let v = null;
  try {
    v = sessionStorage.getItem(TG_DISMISS_KEY);
  } catch (e) {
    return false;
  }
  if (!v) return false;
  if (v === 'reserve') return mode !== 'add';
  return true;
}

// The pairs she closed (HB 2026-10-13): a set is away for the visit when its pair (style to style, data-pair) was on
// offer at the moment she pressed any set's x. Another color of a closed pair stays away; a pair not closed yet
// (another swimsuit style with its top) is a new offer. The match handles of the closed sets are returned too, for
// the callers that key on them.
function dismissedSets(sets) {
  const d = readStore(SET_DISMISS_KEY, null);
  const pairs = new Set(d && Array.isArray(d.pairs) ? d.pairs : []);
  const out = new Set();
  if (!pairs.size) return out;
  sets.forEach((set) => {
    if (pairs.has(set.dataset.pair || '')) out.add(set.dataset.matchHandle);
  });
  return out;
}

function dismissSet(set) {
  const d = readStore(SET_DISMISS_KEY, { pairs: [] });
  if (!Array.isArray(d.pairs)) d.pairs = [];
  // Every pair on offer right now closes with this one (Vic: at most one suggestion, never the next one rising)
  setEls().forEach((other) => {
    const pair = other.dataset.pair || '';
    if (pair && !d.pairs.includes(pair)) d.pairs.push(pair);
  });
  if (set.dataset.pair && !d.pairs.includes(set.dataset.pair)) d.pairs.push(set.dataset.pair);
  writeStore(SET_DISMISS_KEY, d);
  hold(); // the swap in the slot never changes the open sheet's height (the gift's own entrance grows it if it must)
  manageSlot(false);
}

function dismissGift() {
  const gift = giftEl();
  const mode = gift ? giftMode(gift) : 'add';
  try {
    sessionStorage.setItem(TG_DISMISS_KEY, mode === 'add' ? 'add' : 'reserve');
  } catch (e) {
    /* for this page, then */
  }
  // HB 2026-10-13 (Vic): nothing rises in the gift's place; a set may have its turn when the cart is opened again
  state.slotShut = true;
  hold(); // the swap in the slot never changes the open sheet's height
  if (gift && mode === 'add') {
    // HB 2026-10-10 (Vic, 14:20 CPH): closed over the amount, the module folds to one quiet line at the foot of the cart
    // ("Our gift to you", "Choose my gift"), so she is never locked out of her gift: it fades, the line rises in.
    gift.classList.add('is-leaving');
    clearTimeout(state.compactTimer);
    state.compactTimer = setTimeout(() => {
      if (!gift.isConnected) return;
      gift.classList.remove('is-leaving');
      manageSlot(false);
      gift.classList.add('is-entering');
      setTimeout(() => gift.isConnected && gift.classList.remove('is-entering'), 600);
    }, SLOT_SWAP_MS);
    return;
  }
  manageSlot(false);
}

// HB 2026-10-10 (Vic, round 29: "pressing 'Choose my gift' should open the in-cart gift selector and not the big
// slide-up"): the folded line's link forgets the x and gives the module its entrance again. It unfolds into the slot
// the way it does after her accepted set took the cart over the amount (riseGift: measured at full size, the held sheet
// growing by the room it needs, up to the top bar's lower edge), and a set on show gives the slot up to it.
function unfoldGift() {
  const gift = giftEl();
  if (!gift) return;
  try {
    sessionStorage.removeItem(TG_DISMISS_KEY);
  } catch (e) {
    /* for this page, then */
  }
  clearTimeout(state.compactTimer);
  hold();
  gift.classList.remove('is-compact', 'is-leaving', 'is-entering');
  gift.classList.add('is-yield'); // from folded to its full entrance
  state.giftLeftAt = 0;
  manageSlot(false);
}

// A gift closed over the amount stands as its one line (is-compact, below the sets and above the footer by CSS order);
// it does not take the slot, a set may.
function giftCompact(gift) {
  const mode = giftMode(gift);
  return mode === 'add' && giftDismissed(mode);
}

// Which set has the turn (HB 2026-10-13): a swimsuit's top before anything else (data-rank 0: "... Swimsuit > ... Top",
// Vic: "Always preference for the Swimsuit (Bandeau or V-) match with Top (Bandeau or Adjustable)"), then the set for
// the piece she added most recently (the sets come newest piece first). No rotation: it stays until she takes it,
// closes it or its piece leaves the bag.
function chooseSet(cands) {
  const first = cands.find((set) => (set.dataset.rank || '1') === '0');
  return first || cands[0];
}

function giftMode(gift) {
  const inner = gift.querySelector('.hb-set__in');
  return (inner && inner.dataset.mode) || '';
}

function manageSlot(opening) {
  const gift = giftEl();
  const sets = setEls();
  if (state.hadGift && !gift) state.giftLeftAt = Date.now();
  state.hadGift = !!gift;
  if (!sets.length) state.seenSets.clear();
  const pins = pinsEl();
  const pinsOn = !!(pins && !pinsDismissed(pins) && !document.documentElement.classList.contains('hb-pins-off'));
  if (!gift && !sets.length && !pins) return;
  const dismissed = dismissedSets(sets);
  sets.forEach((set) => {
    // The piece she has just removed is not offered back while the cart is open (Undo is her way back).
    const out = state.declined.has(set.dataset.matchHandle);
    if (set.classList.contains('is-declined') !== out) set.classList.toggle('is-declined', out);
    followSize(set);
  });
  // A set she closed with its x leaves through the slot (it fades, the next one rises), so it is not display:none'd here.
  const cands = sets.filter((set) => !set.classList.contains('is-declined') && !dismissed.has(set.dataset.matchHandle));
  let giftTurn = false;
  const compact = !!(gift && giftCompact(gift));
  if (gift) {
    if (compact) {
      if (!gift.classList.contains('is-leaving')) gift.classList.remove('is-yield');
      gift.classList.add('is-compact');
    } else {
      gift.classList.remove('is-compact');
    }
  }
  if (gift && !giftDismissed(giftMode(gift))) {
    const mode = giftMode(gift);
    const was = gift.dataset.hbMode || '';
    if (mode !== was) {
      gift.dataset.hbMode = mode; // kept through the morph (data-skip-node-update)
      // Reserved just now, in front of her: the confirmation is read before a set takes the slot.
      if (mode === 'reserved' && was && was !== 'reserved') gift.dataset.hbReservedAt = String(Date.now());
    }
    if (giftLeavingForBag(gift)) giftTurn = false;
    else if (mode !== 'reserved' || !cands.length) giftTurn = true;
    else {
      const at = Number(gift.dataset.hbReservedAt) || 0;
      const left = at ? RESERVED_HOLD_MS - (Date.now() - at) : 0;
      if (left > 0) {
        giftTurn = true;
        clearTimeout(state.slotTimer);
        state.slotTimer = setTimeout(() => manageSlot(false), left + 20);
      }
    }
  }
  // HB 2026-10-12: the pins belong to the gift field: with the gift's turn they stand above it (compact), without it
  // they have the turn themselves; a set waits until both have been answered.
  // HB 2026-10-13: after the gift's x the slot stays empty until the cart has been closed and opened again
  const shut = state.slotShut && !!(dialogEl() && dialogEl().open);
  const pick = giftTurn ? gift : pinsOn || shut ? null : cands.length ? chooseSet(cands) : null;
  // The compact gift line stands outside the slot: it is neither shown in it nor hidden by it.
  showInSlot(pick, (compact ? [] : [gift]).concat(sets).filter(Boolean));
  showPins(pins, pinsOn, !!(pinsOn && giftTurn && gift));
}

function showInSlot(pick, all) {
  const open = !!(dialogEl() && dialogEl().open);
  const leaving = all.filter((el) => el !== pick && !el.classList.contains('is-yield'));
  leaving.forEach((el) => {
    if (el.classList.contains('is-leaving') || el.classList.contains('is-folding')) return; // already on its way
    if (!open) {
      el.classList.add('is-yield');
      return;
    }
    el.classList.add('is-leaving');
    setTimeout(() => {
      if (!el.isConnected) return;
      el.classList.add('is-yield');
      el.classList.remove('is-leaving');
    }, SLOT_SWAP_MS);
  });
  // An entrance on its way for another module than the one picked now is called off.
  if (state.slotReveal && (!pick || pick.id !== state.slotRevealFor)) {
    clearTimeout(state.slotReveal);
    state.slotReveal = 0;
    state.slotRevealFor = null;
  }
  if (!pick) return;
  if (!pick.classList.contains('is-yield')) {
    if (pick !== giftEl()) handleSetShown(pick, false);
    return;
  }
  if (state.slotRevealFor === pick.id && state.slotReveal) return; // its entrance is already on its way
  const giftGone = state.giftLeftAt && Date.now() - state.giftLeftAt < 2000 ? GIFT_GONE_WAIT_MS : 0;
  let wait = open ? (leaving.length ? SLOT_SWAP_MS : 0) + giftGone : 0;
  if (open && pick === giftEl()) {
    // HB 2026-10-10 (Vic): the gift offered as her accepted set took the cart over the amount. Her new line lands first
    // ("Added to your order"); the module unfolds 1.5 s after it, as the set itself rises after an add.
    const since = state.justAdded ? 0 : state.landedAt ? performance.now() - state.landedAt : Infinity;
    if (RISE_DELAY_MS - since > 0) wait = Math.max(wait, RISE_DELAY_MS - since, 250);
  }
  clearTimeout(state.slotReveal);
  const reveal = () => {
    state.slotReveal = 0;
    state.slotRevealFor = null;
    if (!pick.isConnected) return;
    if (pick === giftEl()) {
      riseGift(pick);
    } else {
      pick.classList.remove('is-yield', 'is-leaving');
      handleSetShown(pick, true);
    }
  };
  if (wait) {
    state.slotRevealFor = pick.id;
    state.slotReveal = setTimeout(reveal, wait);
  } else {
    reveal();
  }
}

function selectSetSize(pill) {
  const group = pill.closest('.hb-set__sizes');
  if (!group || pill.disabled) return;
  group.querySelectorAll('[data-hb-set-variant]').forEach((p) => {
    const on = p === pill;
    p.classList.toggle('is-sel', on);
    p.setAttribute('aria-checked', on ? 'true' : 'false');
  });
}

function rise(set) {
  if (!set || !set.isConnected || !set.classList.contains('is-waiting')) return;
  // HB 2026-10-10 (Vic, round 29): laid out at full size for one unseen frame, so a held sheet grows with the set by
  // the room it needs (the set, like the gift, never takes its room from the line above it).
  set.classList.add('is-measuring');
  const h = set.scrollHeight;
  growHeldSheet();
  set.classList.remove('is-measuring');
  void set.offsetHeight;
  set.style.setProperty('--hb-h', `${h}px`);
  set.classList.add('is-rising');
  set.classList.remove('is-waiting');
  setTimeout(() => {
    if (!set.isConnected) return;
    set.classList.remove('is-rising');
    set.style.removeProperty('--hb-h');
    settleSqueeze();
  }, RISE_MS + 60);
}

// HB 2026-10-10 (Vic, the upsell → gift flow): the gift module enters the open cart the way the set rises after an
// add, unfolding over 0.8 s. In a held sheet (a phone, a change already made inside the open cart) the sheet grows in
// the same movement, by the room the module needs beyond what the lines can spare and never past GIFT_CAP of the
// screen (Vic: "it can grow from the 60% -> 80% to give the needed room for the gift ... not to 100%"). The one
// change inside the open cart that is allowed to change its height. A cart that is not open shows it at once.
const GIFT_CAP = 0.84; // only if the top bar cannot be measured
let growTimer = 0;

// How tall the sheet may grow: up to the lower edge of the top bar (the logo, the bag, the menu), which stays in view
// above it (Vic, 10 Oct 14:20 CPH: the line that aligns with the top bar is the right one). The bar is sticky, so its
// edge is read where it is now; a bar scrolled out of view counts by its height. Without a bar, GIFT_CAP of the screen.
function sheetCap() {
  const hc = document.querySelector('header-component');
  const bar = hc ? hc.closest('header') || hc : null;
  if (!bar) return Math.round(window.innerHeight * GIFT_CAP);
  const r = bar.getBoundingClientRect();
  const edge = r.bottom > 0 && r.bottom < window.innerHeight / 2 ? r.bottom : r.height;
  return Math.round(window.innerHeight - edge);
}

// The held sheet grows by what its lines are squeezed by right now (measured with the newcomer laid out at full size),
// over RISE_MS in the set's curve, never past sheetCap(). Also for the gift bag floating into a held sheet
// (hb:tg-arrive from assets/hb-tgift.js): her gift must not arrive below the fold of the lines.
// HB 2026-10-20 (Vic, his iPhone in the app's browser, one line, her bag just in from the gift module and the
// suggestion risen under it: the bag's picture cut by the fold; "the pictures should fit cleanly and not require
// scrolling when there is only 2 items in cart and 1 product suggestion"): the top bar's lower edge stays the sheet's
// first stop. When the lines would still be squeezed there, the sheet grows on to the full screen in the same
// movement, the whole way (a sliver of the page left above it is no stop): the height a cart opened with two lines
// has anyway (hb-cart--full, inset 0), the lines taking the room. Only past that do the lines scroll (round 27). On
// a tall phone the first stop is enough and nothing changes.
function growHeldSheet() {
  const dlg = dialogEl();
  const root = document.documentElement;
  if (!dlg || !dlg.open || !phone() || !root.classList.contains('hb-cart-held') || !dlg.classList.contains('hb-cart--sheet')) return 0;
  const items = dlg.querySelector('.cart-drawer__items');
  const squeeze = items ? Math.max(0, items.scrollHeight - items.clientHeight) : 0;
  const now = parseInt(root.style.getPropertyValue('--hb-cart-h'), 10) || Math.round(dlg.getBoundingClientRect().height);
  let cap = sheetCap();
  let grow = Math.max(0, Math.min(squeeze, cap - now));
  if (now + squeeze > cap && window.innerHeight > cap) {
    cap = Math.round(window.innerHeight);
    grow = Math.max(0, cap - now); // the whole way: a sliver of the page above the sheet is no stop
  }
  if (!grow) return 0;
  root.classList.add('hb-cart-growing');
  root.style.setProperty('--hb-cart-h', `${now + grow}px`);
  clearTimeout(growTimer);
  growTimer = setTimeout(() => root.classList.remove('hb-cart-growing'), RISE_MS + 60);
  return grow;
}

function riseGift(gift) {
  if (!gift || !gift.isConnected) return;
  const dlg = dialogEl();
  if (!dlg || !dlg.open) {
    gift.classList.remove('is-yield', 'is-leaving', 'is-waiting', 'is-rising', 'is-measuring');
    return;
  }
  if (gift.classList.contains('is-leaving')) return; // on its way out (its bag is coming): not brought back
  // Measured at its full size before a frame is drawn: its height, and how much the lines would be squeezed by it.
  gift.classList.remove('is-yield');
  gift.classList.add('is-measuring');
  const h = gift.offsetHeight;
  growHeldSheet();
  gift.classList.remove('is-measuring');
  gift.classList.add('is-waiting');
  void gift.offsetHeight;
  gift.style.setProperty('--hb-h', `${h}px`);
  gift.classList.add('is-rising');
  gift.classList.remove('is-waiting');
  setTimeout(() => {
    if (!gift.isConnected) return;
    gift.classList.remove('is-rising');
    gift.style.removeProperty('--hb-h');
    settleSqueeze();
  }, RISE_MS + 60);
}

// HB 2026-10-12: the same unfolding for the pins module (snippets/hb-cart-pins; its classes in assets/hb-pins.css).
function riseModule(el) {
  if (!el || !el.isConnected) return;
  const dlg = dialogEl();
  if (!dlg || !dlg.open) {
    el.classList.remove('is-yield', 'is-leaving', 'is-waiting', 'is-rising', 'is-measuring');
    return;
  }
  el.classList.remove('is-yield', 'is-leaving');
  el.classList.add('is-measuring');
  const h = el.offsetHeight;
  growHeldSheet();
  el.classList.remove('is-measuring');
  el.classList.add('is-waiting');
  void el.offsetHeight;
  el.style.setProperty('--hb-h', `${h}px`);
  el.classList.add('is-rising');
  el.classList.remove('is-waiting');
  setTimeout(() => {
    if (!el.isConnected) return;
    el.classList.remove('is-rising');
    el.style.removeProperty('--hb-h');
    settleSqueeze();
  }, RISE_MS + 60);
}

// HB 2026-10-10 (Vic, round 29: the module came up over the trousers' line on his phone): once a module stands at
// its full height in a held sheet, the lines are measured once more and the sheet grows by whatever still squeezes
// them (up to the top bar's lower edge). A suggestion never cuts the piece above it; past the cap the lines scroll.
function settleSqueeze() {
  const dlg = dialogEl();
  if (!dlg || !dlg.open) return;
  const items = dlg.querySelector('.cart-drawer__items');
  if (!items || items.scrollHeight - items.clientHeight < 2) return;
  growHeldSheet();
}

// A reserved gift with the cart over the amount is on its way into the cart as a bag (assets/hb-tgift.js adds it by
// itself): the module is not given the slot meanwhile.
function giftLeavingForBag(gift) {
  if (gift.classList.contains('is-leaving')) return true;
  const cur = window.HBGift && window.HBGift.state && window.HBGift.state.cur;
  return !!(giftMode(gift) === 'reserved' && cur && cur.thr > 0 && cur.sub >= cur.thr && !cur.bagKey);
}

async function lightenClip(set) {
  const video = set.querySelector('[data-hb-set-video]');
  if (!video || video.dataset.hbReady) return;
  // HB 2026-10-09 (Vic, page weight): the clip is fetched the first time the cart opens, not with every page.
  // A closed cart holds a video element with no source; this runs again from the open observer (init).
  const dlgNow = dialogEl();
  if (!dlgNow || !dlgNow.open) return;
  video.dataset.hbReady = '1';
  let src = null;
  try {
    const res = await fetch(`${ROOT}products/${encodeURIComponent(set.dataset.matchHandle)}.js`, { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const product = await res.json();
      const media = (product.media || []).find((m) => m.media_type === 'video' && Array.isArray(m.sources));
      if (media) {
        const hls = media.sources.find((s) => s.format === 'm3u8');
        if (hls && video.canPlayType('application/vnd.apple.mpegurl')) src = { url: hls.url, type: 'application/vnd.apple.mpegurl' };
        if (!src) {
          // The smallest mp4 that is still sharp at 72 x 96 on a phone (the stage is tiny; the 1080p file is not needed).
          const mp4s = media.sources.filter((s) => s.format === 'mp4' && s.height).sort((a, b) => a.height - b.height);
          const pick = mp4s.find((s) => s.height >= 360) || mp4s[mp4s.length - 1];
          if (pick) src = { url: pick.url, type: 'video/mp4' };
        }
      }
    }
  } catch (e) {
    src = null;
  }
  if (!src && video.dataset.fallbackSrc) src = { url: video.dataset.fallbackSrc, type: video.dataset.fallbackType || 'video/mp4' };
  if (!src || !video.isConnected) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const source = document.createElement('source');
  source.src = src.url;
  source.type = src.type;
  video.appendChild(source);
  video.muted = true;
  video.preload = 'auto';
  video.load();
  const play = () => {
    const p = video.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  if (video.readyState >= 2) play();
  else video.addEventListener('canplay', play, { once: true });
}

// The set's ring follows her size on the line above, until she picks a size in the set herself.
function followSize(set) {
  const pre = set.querySelector('.hb-set__col')?.dataset.hbPre;
  if (!pre || pre === set.dataset.hbPre) return;
  const first = !set.dataset.hbPre;
  set.dataset.hbPre = pre; // .hb-set keeps its attributes through the morph (data-skip-node-update)
  if (first || set.dataset.hbPicked) return;
  const pill = set.querySelector(`.hb-set__sizes [data-hb-set-variant="${CSS.escape(pre)}"]`);
  if (pill && !pill.disabled) selectSetSize(pill);
}

// The set that has the turn: its first appearance with an add rises from the footer; any other first appearance,
// or a turn taken while the cart is open, comes in softly.
function handleSetShown(set, entering) {
  const fresh = !state.seenSets.has(set.id);
  if (fresh) state.seenSets.add(set.id);
  const open = !!(dialogEl() && dialogEl().open);
  if (fresh) {
    const sinceAdd = performance.now() - state.armedAt;
    if (state.armedAt && sinceAdd < ARM_WINDOW_MS) {
      // A set that appears with an add waits, then rises from the footer (the sheet grows upward).
      set.classList.add('is-waiting');
      clearTimeout(state.riseTimer);
      const wait = Math.max(250, RISE_DELAY_MS - sinceAdd);
      state.riseTimer = setTimeout(() => rise(set), wait);
      document.dispatchEvent(new CustomEvent('hb:match', { detail: { action: 'shown', match: set.dataset.matchHandle, trigger: set.dataset.triggerHandle } }));
      lightenClip(set);
      return;
    }
  }
  if ((fresh || entering) && open) {
    set.classList.add('is-entering');
    // HB 2026-10-10 (Vic, round 29): a set taking the slot in a held sheet gets its room as it fades in.
    growHeldSheet();
    setTimeout(() => {
      if (!set.isConnected) return;
      set.classList.remove('is-entering');
      settleSqueeze();
    }, 700);
  }
  lightenClip(set);
}

async function addSet(btn) {
  const set = btn.closest('[data-hb-set]');
  if (!set || state.busy || btn.disabled) return;
  let variantId;
  // HB 2026-10-11: a piece with two options (the Knit Triangle Bikini: Top and Bottom) shows one row of sizes per
  // option; the variant is the one for her two picks (snippets/hb-cart-set.liquid, data-hb-set-combos "Top~Bottom=id;").
  const opts = set.querySelector('[data-hb-set-combos]');
  if (opts) {
    const picks = [...opts.querySelectorAll('.hb-set__sizes')].map(
      (g) => g.querySelector('.is-sel[data-hb-set-variant]')?.dataset.hbSetVariant
    );
    if (!picks.length || picks.some((v) => !v)) return;
    const combos = {};
    (opts.dataset.hbSetCombos || '').split(';').forEach((c) => {
      const i = c.lastIndexOf('=');
      if (i > 0) combos[c.slice(0, i)] = c.slice(i + 1);
    });
    variantId = Number(combos[picks.join('~')]);
    if (!variantId) {
      const note = set.querySelector('[data-hb-set-msg]');
      if (note) {
        note.textContent = 'Not available in this size right now.';
        note.hidden = false;
        setTimeout(() => {
          if (note.isConnected) note.hidden = true;
        }, 4000);
      }
      return;
    }
  } else {
    const chosen = set.querySelector('.hb-set__sizes .is-sel[data-hb-set-variant], .hb-set__onesize[data-hb-set-variant]');
    if (!chosen) return;
    variantId = Number(chosen.dataset.hbSetVariant);
  }
  const handle = set.dataset.matchHandle;
  hold();
  const triggerLine = document.querySelector(
    `cart-drawer-component [data-hb-line][data-handle="${CSS.escape(set.dataset.triggerHandle || '')}"]`
  );
  state.busy = true;
  btn.disabled = true;
  // The set's line folds away while the sizes and ADD TO MY ORDER fade (the add runs meanwhile).
  const kicker = set.querySelector('.hb-set__line');
  if (kicker) {
    kicker.style.maxHeight = `${kicker.offsetHeight}px`;
    void kicker.offsetHeight;
  }
  set.classList.add('is-adding');
  try {
    // HB 2026-10-09 (Vic): one invitation is enough. The cart attribute is written before the add, so the drawer
    // that comes back with the add already sees "added:<handle>" (snippets/hb-cart-set.liquid) and offers nothing
    // further while that piece is in the bag: no belted top straight after the trousers.
    await fetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { hb_match: `added:${handle}` } }),
    }).catch(() => {});
    const [data] = await Promise.all([
      cartPost('cart/add.js', { items: [{ id: variantId, quantity: 1 }] }),
      new Promise((resolve) => setTimeout(resolve, SET_FOLD_MS)),
    ]);
    if (typeof window.triggerKlaviyoAddedToCart === 'function') {
      try {
        window.triggerKlaviyoAddedToCart(data);
      } catch (e) {
        /* tracking never breaks the add */
      }
    }
    document.dispatchEvent(new CustomEvent('hb:match', { detail: { action: 'added', match: handle, trigger: set.dataset.triggerHandle, variantId } }));

    // Her new line takes the set's place: right under the piece it belongs to, not at the top.
    const newKey = data && data.items && data.items[0] && data.items[0].key;
    if (newKey) {
      const already = state.order.includes(newKey);
      if (!already && triggerLine) {
        const at = state.order.indexOf(triggerLine.dataset.key);
        state.order.splice(at === -1 ? state.order.length : at + 1, 0, newKey);
        saveOrder();
      }
      state.justAdded = newKey;
      // Where everything is now, so the new line can start exactly where the set's row stands.
      const flip = {};
      document.querySelectorAll('cart-drawer-component [data-hb-line]').forEach((l) => {
        flip[l.dataset.key] = l.getBoundingClientRect().top;
      });
      const row = set.querySelector('.hb-set__row');
      if (row) flip[newKey] = row.getBoundingClientRect().top - 14; // the line's 14 px padding above its picture
      state.flip = flip;
    }
    // HB 2026-10-12: hbAdd marks her own add of a piece (assets/hb-tgift.js: the gift's tile after an add)
    publish(data, { productId: undefined, variantId: String(variantId), hbAdd: 'set' });
  } catch (err) {
    set.classList.remove('is-adding');
    if (kicker) kicker.style.maxHeight = '';
    btn.disabled = false;
    const msg = set.querySelector('[data-hb-set-msg]');
    if (msg) {
      msg.textContent = 'Not available in this size right now.';
      msg.hidden = false;
      setTimeout(() => {
        if (msg.isConnected) msg.hidden = true;
      }, 4000);
    }
  } finally {
    state.busy = false;
  }
}

// The note on the set's piece once it is a line: "Added to your order", for 3 seconds.
function noteAdded() {
  const key = state.justAdded;
  if (!key) return;
  const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(key)}"]`);
  if (!line) return;
  state.justAdded = null;
  state.landedAt = performance.now(); // the gift module, if it is next in the slot, keeps its 1.5 s from here
  // FLIP: every line starts where it stood and glides to its new place (the new one from the set's row).
  const flip = state.flip;
  state.flip = null;
  if (flip) {
    document.querySelectorAll('cart-drawer-component [data-hb-line]').forEach((l) => {
      const before = flip[l.dataset.key];
      if (typeof before !== 'number') return;
      const delta = before - l.getBoundingClientRect().top;
      if (Math.abs(delta) < 1) return;
      l.style.transition = 'none';
      l.style.transform = `translateY(${delta}px)`;
      void l.offsetHeight;
      l.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      l.style.transform = '';
      setTimeout(() => {
        l.style.transition = '';
      }, 520);
    });
  }
  // What changed elsewhere (the count, the total) comes in softly too.
  document
    .querySelectorAll('cart-drawer-component #cart-drawer-heading, cart-drawer-component .hb-checkout__amt')
    .forEach((el) => {
      el.classList.remove('hb-flash');
      void el.offsetWidth;
      el.classList.add('hb-flash');
      setTimeout(() => el.classList.remove('hb-flash'), 950);
    });
  line.classList.add('is-new');
  setTimeout(() => line.classList.remove('is-new'), 700);
  state.noted = { key, until: Date.now() + ADDED_NOTE_MS };
  placeNote(line, ADDED_NOTE_MS);
}

// "Added to your order" on the line, for the time left of its 3 s. The line is rendered anew when Shopify renews its
// key (the gift bag arriving changes the discount's allocation), so the note is put back on it (keepNote, afterRender).
function placeNote(line, left) {
  const acts = line.querySelector('.hb-line__acts');
  if (!acts || acts.querySelector('.hb-line__added')) return;
  const note = document.createElement('span');
  note.className = 'hb-line__added';
  note.setAttribute('role', 'status');
  note.innerHTML = `${CHECK_SVG}<span>Added to your order</span>`;
  acts.prepend(note);
  setTimeout(() => {
    note.classList.add('is-gone');
    setTimeout(() => note.remove(), 420);
  }, Math.max(0, left));
}

function keepNote() {
  const n = state.noted;
  if (!n) return;
  const left = n.until - Date.now();
  if (left < 300) {
    state.noted = null;
    return;
  }
  const line = document.querySelector(`cart-drawer-component [data-hb-line][data-key="${CSS.escape(n.key)}"]`);
  if (line) placeNote(line, left);
}

/* ---------- the empty cart: Recently viewed ---------- */

function viewedHandles(without) {
  let list = [];
  try {
    list = JSON.parse(window.localStorage.getItem('viewedProducts') || '[]');
  } catch (e) {
    list = [];
  }
  return (Array.isArray(list) ? list : []).filter((h) => typeof h === 'string' && /^[a-z0-9-]+$/.test(h) && !state.removedHandles.has(h) && h !== without);
}

// HB 2026-10-12 (round 30, audit item 5): each viewed piece's line is kept for ten minutes in this tab (sessionStorage),
// so a page view no longer fetches the six lines again (6 requests and about 150 KB on every page of every visitor);
// the list is still there, complete, the moment the cart opens or empties. Ten minutes keeps prices and stock fresh.
const RECENT_CACHE = 'hb-cart-recent-v1';
const RECENT_TTL = 10 * 60 * 1000;
function recentCache() {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(RECENT_CACHE) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch (e) {
    return {};
  }
}
function recentLine(h) {
  const hit = recentCache()[h];
  if (hit && typeof hit.html === 'string' && Date.now() - hit.t < RECENT_TTL) return Promise.resolve(hit.html);
  return fetch(`${ROOT}products/${encodeURIComponent(h)}?view=hb-cart-recent`, { headers: { Accept: 'text/html' } })
    .then((r) => {
      if (!r.ok && r.status !== 404) return '';
      return (r.ok ? r.text() : Promise.resolve('')).then((t) => {
        try {
          const all = recentCache();
          const now = Date.now();
          Object.keys(all).forEach((k) => {
            if (!all[k] || now - all[k].t >= RECENT_TTL) delete all[k];
          });
          all[h] = { t: now, html: t };
          window.sessionStorage.setItem(RECENT_CACHE, JSON.stringify(all));
        } catch (e) {
          /* storage full or blocked: fetched again next time */
        }
        return t;
      });
    })
    .catch(() => '');
}

// The lines for the empty cart (up to six candidates, the first three that are in stock).
// `without`: a piece still in the cart that is about to go (fetched ahead as if it were gone, so the list matches).
function loadRecent(without) {
  const handles = viewedHandles(without).slice(0, 6);
  const sig = handles.join(',');
  if (state.recent && state.recent.sig === sig) return state.recent.promise;
  const promise = Promise.all(handles.map((h) => recentLine(h))).then((htmls) =>
    htmls.map((t) => t.trim()).filter((t) => t.includes('data-hb-recent-line')).slice(0, RECENT_MAX)
  );
  state.recent = { sig, promise };
  return promise;
}

// Only the list is filled here. hb-cart.css shows "Recently viewed" and swaps the doors' line once the list
// holds two or more pieces; the list keeps its children through the drawer's morph (data-skip-subtree-update),
// and a property on the element (not an attribute, which the morph resets) remembers what is in it.
async function fillRecent() {
  const list = document.querySelector('cart-drawer-component [data-hb-recent-lines]');
  if (!list) return;
  const lines = await loadRecent();
  if (!list.isConnected) return;
  const want = lines.length >= RECENT_MIN ? lines : [];
  if (list.hbSig !== state.recent.sig || list.childElementCount !== want.length) {
    list.innerHTML = want.join('');
    list.hbSig = state.recent.sig;
  }
  if (document.documentElement.classList.contains('hb-cart-filling')) revealEmptied();
  // In a held sheet (her last piece removed inside the cart) the empty cart shows the three doors only, sized to
  // the room under the Undo row (hb-cart.css): Vic, 2026-10-08, more useful there than one viewed piece squeezed in.
}

// The moment her cart empties inside the open sheet, the empty cart fades in under the Undo row.
// Vic, 2026-10-08 (desktop): the last Remove must flow into the empty cart in one movement. The cart's foot fades
// as the line folds (removeLine); the empty cart then waits, hidden, for her viewed pieces (fetched from the moment
// she tapped, usually there already), so it comes in once, in its final layout, instead of the doors appearing and
// then being pushed down when the pieces arrive. At most 1.2 s. In the held sheet (phones) the empty cart shows
// the doors only, so nothing needs waiting for.
function markEmptied() {
  const empty = !!document.querySelector('cart-drawer-component .hb-empty');
  const dlg = dialogEl();
  if (empty && state.wasEmpty === false && dlg && dlg.open) {
    const root = document.documentElement;
    if (!root.classList.contains('hb-cart-held')) {
      root.classList.add('hb-cart-filling');
      clearTimeout(state.fillTimer);
      state.fillTimer = setTimeout(revealEmptied, 1200);
    } else {
      revealEmptied();
    }
  }
  state.wasEmpty = empty;
}

function revealEmptied() {
  const root = document.documentElement;
  clearTimeout(state.fillTimer);
  root.classList.remove('hb-cart-filling');
  root.classList.add('hb-cart-emptied');
  clearTimeout(state.emptiedTimer);
  state.emptiedTimer = setTimeout(() => root.classList.remove('hb-cart-emptied'), 900);
}

/* ---------- after every re-render ---------- */

function fillArrives() {
  const text = window.HBArrives;
  if (!text) return;
  document.querySelectorAll('[data-hb-arrives]').forEach((el) => {
    if (el.textContent !== text) el.textContent = text;
  });
}

let scheduled = false;
function afterRender() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    keepPageOpen();
    enforceMode();
    markEmptied();
    applyOrder();
    fillArrives();
    manageSlot(false);
    placeUndo();
    settleRestored();
    noteAdded();
    keepNote();
    fillRecent();
  });
}

/* ---------- wiring ---------- */

function onClick(event) {
  const t = event.target;
  if (!(t instanceof Element)) return;
  if (!t.closest('cart-drawer-component')) return;

  const opt = t.closest('.hb-opt[data-hb-opt]');
  if (opt) {
    event.preventDefault();
    toggleSizes(opt);
    return;
  }
  const pill = t.closest('[data-hb-pills] .hb-pill');
  if (pill) {
    event.preventDefault();
    if (!pill.disabled) swapVariant(pill);
    return;
  }
  const qty = t.closest('[data-hb-qty]');
  const shifting = performance.now() < state.guardUntil;
  if (qty) {
    event.preventDefault();
    if (!qty.disabled && !shifting) changeQuantity(qty);
    return;
  }
  const remove = t.closest('[data-hb-remove]');
  if (remove) {
    event.preventDefault();
    if (!shifting) removeLine(remove);
    return;
  }
  const undoBtn = t.closest('[data-hb-undo-btn]');
  if (undoBtn) {
    event.preventDefault();
    undo(undoBtn);
    return;
  }
  const setPill = t.closest('.hb-set__sizes [data-hb-set-variant]');
  if (setPill) {
    event.preventDefault();
    const set = setPill.closest('[data-hb-set]');
    if (set && !setPill.disabled) set.dataset.hbPicked = '1';
    selectSetSize(setPill);
    return;
  }
  const add = t.closest('[data-hb-set-add]');
  if (add) {
    event.preventDefault();
    addSet(add);
    return;
  }
  // HB 2026-10-10 (Vic): the small x on a suggestion.
  const xSet = t.closest('[data-hb-set-dismiss]');
  if (xSet) {
    event.preventDefault();
    const set = xSet.closest('[data-hb-set]');
    if (set) dismissSet(set);
    return;
  }
  if (t.closest('[data-hb-tg-dismiss]')) {
    event.preventDefault();
    dismissGift();
    return;
  }
  // HB 2026-10-12: the small x on the pins module.
  if (t.closest('[data-hb-pins-dismiss]')) {
    event.preventDefault();
    dismissPins();
    return;
  }
  // HB 2026-10-10 (Vic, round 29): "Choose my gift" on the folded line brings the module itself back into the cart.
  if (t.closest('[data-hb-tg-unfold]')) {
    event.preventDefault();
    unfoldGift();
    return;
  }
  // A tap anywhere else in the drawer closes an open size row.
  if (!t.closest('[data-hb-pills]')) closeAllSizes();
}

function onCartUpdate(event) {
  const data = (event.detail && event.detail.data) || {};
  if (data.didError) return;
  // The collection cards' quick add (assets/product-custom.js) passes its source as the event's resource; it
  // does not open the cart (see quickAddStaysClosed), so it does not arm the set's rise either.
  const resourceSource = event.detail && event.detail.resource && event.detail.resource.source;
  if (resourceSource === 'variant-popup-inner') return;
  const isAdd =
    event instanceof CartAddEvent ||
    data.source === 'product-form-component' ||
    data.source === 'quick-add';
  if (isAdd && !ADD_SOURCES_IGNORED.has(data.source)) {
    state.armedAt = performance.now();
    state.keepMode = null; // an add from the page: the cart chooses its height again as it opens
  }
}

// Quick add never opens the cart (Vic, 2026-10-08): it is there so she can keep shopping. The drawer opens on
// every cart:update while it carries auto-open, so for the quick add's event the attribute is lifted for that
// one dispatch (a listener on window in the capture phase runs before the drawer's own on document). The
// drawer's content and the bag count still update; the "+" on the card turns into a check (product-custom.js).
function quickAddStaysClosed(event) {
  const source = event.detail && event.detail.resource && event.detail.resource.source;
  if (source !== 'variant-popup-inner') return;
  showToast();
  const root = drawer();
  if (!root || !root.hasAttribute('auto-open')) return;
  const value = root.getAttribute('auto-open');
  root.removeAttribute('auto-open');
  setTimeout(() => root.setAttribute('auto-open', value || ''), 0);
}

// After a quick add, a note at the bottom (Vic, 2026-10-08): built like a small cart line on white, the piece's
// picture (36 x 48) and its name, "Added to your cart" with the cart's check, and VIEW CART styled like the set's
// ADD TO MY ORDER; it opens the cart. It leaves by itself after 4 seconds (5 until 12 Oct, Vic), stays while
// the mouse or keyboard focus is on it, and leaves at once when the cart opens. The quick add only sends its
// event after /cart/add.js succeeded, so the note never shows for an add that failed. Not for B2B (their quick
// add sends its own source, and the body carries b2b-store): their buying flow does not change.
const TOAST_MS = 3000; // HB 2026-10-12 (Vic): "could be reduced from 5s to 4s"; HB 2026-10-14 (Vic, round 44): one second less again
const TOAST_CAP_MS = 15000; // HB 2026-10-09: held or not, the note never stays longer than this (Vic: it stuck on his phone)
let toastEl = null;
let toastTimer = 0;
let toastShowTimer = 0;
let toastCapTimer = 0;
let toastHiddenAt = -1000;
let toastHeld = false;
// HB 2026-10-10 (Vic: the gift's note slipped in between a quick add and its own note): from the tap on a quick add
// until its note has left, <html> carries hb-qa-busy and the gift script (assets/hb-tgift.js) holds its note back;
// the tap is also announced (hb:qa-tap) so a gift note already up leaves at once. The mark lifts by itself if no
// note follows (the add failed).
const QA_BUSY_MS = 6000;
let qaBusyTimer = 0;
function qaBusy(on) {
  const root = document.documentElement;
  clearTimeout(qaBusyTimer);
  if (on) {
    root.classList.add('hb-qa-busy');
    qaBusyTimer = setTimeout(() => root.classList.remove('hb-qa-busy'), QA_BUSY_MS);
    document.dispatchEvent(new CustomEvent('hb:qa-tap'));
  } else {
    root.classList.remove('hb-qa-busy');
  }
}

// The piece is read from the quick add she tapped (its sheet, or the card for a piece with one size), in the
// capture phase, before the theme's own handlers stop the click.
function notePiece(event) {
  const t = event.target;
  if (!(t instanceof Element)) return;
  // A new quick add (Vic, 2026-10-08): the note steps aside at once, and the next one rises in after the add.
  if (t.closest('.atc-icon-wrapper .quick-add-btn')) hideToast();
  const btn = t.closest('.hb-qa__add, .atc-icon-wrapper .quick-add-btn[data-variant-id]');
  if (!btn) return;
  qaBusy(true);
  const wrap = btn.closest('.atc-icon-wrapper');
  const qa = btn.closest('.hb-qa') || (wrap && wrap.querySelector('.hb-qa'));
  const card = btn.closest('product-card, .product-card');
  let name = qa && qa.querySelector('.hb-qa__name') ? qa.querySelector('.hb-qa__name').textContent : '';
  if (!name.trim() && card) {
    const title = card.querySelector('.hb-name, [class*="product-card__title"], [class*="title"]');
    name = title ? title.textContent : '';
  }
  let img = qa && qa.querySelector('.hb-qa__img');
  if (!img && card) img = card.querySelector('img');
  state.qaPiece = {
    name: name.replace(/\s+/g, ' ').trim(),
    img: img ? smallPicture(img.currentSrc || img.src || '') : '',
    at: performance.now(),
  };
}

// HB 2026-10-10 (Vic's phone, a reload while flicking with the note up): the note's 36 x 48 picture is asked from the
// CDN at 150 px, not at the card's size (a 544 px picture decodes to about 1.8 MB; this one to a tenth of that).
function smallPicture(url) {
  if (!url || !/cdn\.shopify\.com|\/cdn\/shop\//.test(url)) return url;
  if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=150');
  return url + (url.includes('?') ? '&' : '?') + 'width=150';
}

function makeToast() {
  if (toastEl && toastEl.isConnected) return toastEl;
  if (document.body.classList.contains('b2b-store')) return null;
  const el = document.createElement('div');
  el.className = 'hb-toast';
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML =
    '<span class="hb-toast__media"><img class="hb-toast__img" alt="" width="36" height="48"></span>' +
    '<span class="hb-toast__col">' +
    '<span class="hb-toast__name"></span>' +
    '<span class="hb-toast__band">' +
    `<span class="hb-toast__tx">${CHECK_SVG}<span>Added to your cart</span></span>` +
    '<button type="button" class="hb-toast__go">View cart</button>' +
    '</span>' +
    '</span>';
  // HB 2026-10-09 (Vic): a tap anywhere on the note (picture, name, VIEW CART) opens the cart.
  el.addEventListener('click', (e) => {
    e.preventDefault();
    hideToast();
    // HB 2026-10-22: on the cart page the cart is the page itself: up to it.
    if (pageMode()) {
      const dlg = dialogEl();
      if (dlg) dlg.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const root = drawer();
    if (root && typeof root.open === 'function') root.open();
  });
  const hold = () => {
    toastHeld = true;
    clearTimeout(toastTimer);
  };
  const letGo = () => {
    toastHeld = false;
    armToast();
  };
  // The hover hold is for a real pointer only. On a phone a tap fires mouseenter with no mouseleave to follow,
  // which kept the note on the page for good (Vic, 2026-10-09).
  if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    el.addEventListener('mouseenter', hold);
    el.addEventListener('mouseleave', letGo);
  }
  el.addEventListener('focusin', hold);
  el.addEventListener('focusout', letGo);
  document.body.appendChild(el);
  toastEl = el;
  return el;
}
function armToast() {
  clearTimeout(toastTimer);
  if (toastHeld || !toastEl || !toastEl.classList.contains('is-in')) return;
  toastTimer = setTimeout(hideToast, TOAST_MS);
}
function hideToast() {
  clearTimeout(toastTimer);
  clearTimeout(toastShowTimer);
  clearTimeout(toastCapTimer);
  toastHeld = false;
  if (toastEl && toastEl.classList.contains('is-in')) {
    toastEl.classList.remove('is-in');
    toastHiddenAt = performance.now();
  }
  qaBusy(false);
}
function showToast() {
  const el = makeToast();
  if (!el) return;
  const piece = state.qaPiece && performance.now() - state.qaPiece.at < 15000 ? state.qaPiece : null;
  state.qaPiece = null;
  const name = el.querySelector('.hb-toast__name');
  const img = el.querySelector('.hb-toast__img');
  const media = el.querySelector('.hb-toast__media');
  const fill = () => {
    name.textContent = piece && piece.name ? piece.name : '';
    el.classList.toggle('hb-toast--plain', !(piece && piece.name));
    if (piece && piece.img) {
      img.src = piece.img;
      media.hidden = false;
    } else {
      img.removeAttribute('src');
      media.hidden = true;
    }
  };
  // A note still up (or still fading) finishes leaving first; the new piece never swaps in place.
  hideToast();
  qaBusy(true); // the gift's note keeps waiting through the gap before this note rises
  const wait = Math.max(30, 330 - (performance.now() - toastHiddenAt));
  toastShowTimer = setTimeout(() => {
    fill();
    void el.offsetWidth; // start from the hidden state, so it rises in
    el.classList.add('is-in');
    armToast();
    clearTimeout(toastCapTimer);
    toastCapTimer = setTimeout(hideToast, TOAST_CAP_MS);
  }, wait);
}

// HB 2026-10-22: the cart page's drawer stays open. Other scripts close every open dialog before their own (the gift
// pop-up, assets/gift-popup.js): on this one close does nothing. Set again after each re-render.
function keepPageOpen() {
  if (!pageMode()) return;
  const dlg = dialogEl();
  if (!dlg || dlg.hbKeptOpen) return;
  dlg.hbKeptOpen = true;
  dlg.close = () => {};
  if (!dlg.open) dlg.setAttribute('open', '');
}

let started = false;
function init() {
  const root = drawer();
  // A module-level flag: the drawer's attributes are reset by the theme's morph, so they cannot carry state.
  if (!root || started) return;
  started = true;
  window.HBCart = { version: '2026-10-22', state, unfoldGift, pageMode };
  keepPageOpen();
  state.order = loadOrder();
  state.wasEmpty = !!document.querySelector('cart-drawer-component .hb-empty');
  applyOrder();
  document.addEventListener('click', onClick);
  document.addEventListener(CartAddEvent.eventName || 'cart:update', onCartUpdate, true);
  window.addEventListener(CartAddEvent.eventName || 'cart:update', quickAddStaysClosed, true);
  window.addEventListener('click', notePiece, true);
  // HB 2026-10-10: her gift bag floating into a held sheet (assets/hb-tgift.js) gets its room the same way the module does.
  document.addEventListener('hb:tg-arrive', () => growHeldSheet());
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllSizes();
  });
  new MutationObserver(afterRender).observe(root, { childList: true, subtree: true });
  let wasOpen = !!(dialogEl() && dialogEl().open);
  new MutationObserver(() => {
    const dlg = dialogEl();
    const open = !!(dlg && dlg.open);
    if (open === wasOpen) return;
    wasOpen = open;
    release();
    if (!open) state.keepMode = null;
    else if (!state.keepMode) modeFromLines();
    // Closing: the slot shut by the gift's x opens again for the next look (HB 2026-10-13); no rotation any more.
    if (open) manageSlot(true);
    else {
      state.slotShut = false;
      manageSlot(false);
    }
    // A set she declined earlier (its piece removed) may show again now that the cart opens afresh.
    if (open) afterRender();
    // The set's clip loads now that the cart is open (lightenClip does nothing while it is closed).
    if (open) {
      const openSet = setEl();
      if (openSet) lightenClip(openSet);
    }
    if (open && !(phone() && dlg.classList.contains('hb-cart--sheet'))) {
      const only = document.querySelectorAll('cart-drawer-component .hb-lines > [data-hb-line]');
      if (only.length === 1) loadRecent(only[0].dataset.handle); // ready before her last piece is removed
    }
    // The cart is open (VIEW CART or the bag): the quick add note has done its job.
    if (open) hideToast();
  }).observe(root, { attributes: true, attributeFilter: ['open'], subtree: true });
  // The sets and the gift module already in the page (the drawer is rendered with the page): the slot decides which
  // one shows before the cart first opens; its clip loads once the cart is open (page weight).
  setEls().forEach((set) => {
    state.seenSets.add(set.id);
    followSize(set);
  });
  state.hadGift = !!giftEl();
  manageSlot(false);
  // HB 2026-10-22: the cart page's drawer is open from the start: what opening the drawer does, done once now.
  if (pageMode()) {
    const openSet = setEl();
    if (openSet) lightenClip(openSet);
    afterRender();
  }
  fillArrives();
  setTimeout(fillArrives, 600);
  // HB 2026-10-12 (round 30): the empty cart's viewed pieces are filled when the browser is idle, not while the page
  // is still loading (opening the cart fills them at once in any case, afterRender).
  if (window.requestIdleCallback) window.requestIdleCallback(() => fillRecent(), { timeout: 3000 });
  else setTimeout(fillRecent, 1200);
  // Ready before the cart empties (her last piece removed), so the empty cart comes in complete. With one piece in
  // the cart, the list is fetched as it will be once that piece is removed (it is never offered back right away).
  setTimeout(() => {
    const only = document.querySelectorAll('cart-drawer-component .hb-lines > [data-hb-line]');
    loadRecent(only.length === 1 ? only[0].dataset.handle : undefined);
  }, 2500);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

// HB 2026-10-09 (Vic): the size she taps is remembered in this browser (localStorage "hb-size"), so the product page
// can open on it next time (assets/hb-pdp.js: her size, else M, else the nearest in stock). Only letter sizes.
// Tapped here: the quick add's size rows on the collection pages, and the size pills in the cart's lines.
document.addEventListener('click', (e) => {
  const el = e.target instanceof Element ? e.target.closest('.custom-size-row[data-option-value], .hb-pill[data-variant-id]') : null;
  if (!el) return;
  const v = String(el.getAttribute('data-option-value') || el.textContent || '').trim().toUpperCase();
  if (['M', 'L', 'S', 'XL', 'XS', 'XXL', 'XXS', 'XXXL', '3XL'].indexOf(v) === -1) return;
  try { localStorage.setItem('hb-size', v); } catch (_) {}
}, true);

// HB 2026-10-23 (Vic: "It should still be possible to X this to close it permanently"): the x on "Your gift is waiting"
// (snippets/hb-cart-tgift.liquid) hides that line for good for her gift code in this browser: the code joins the list in
// localStorage "hb-gline-off", and html.hb-gline-off (assets/hb-cart.css) keeps the line away through every re-render of
// the cart and on every page, for as long as that code is hers. Her code: the one assets/gift-popup.js keeps for the visit
// (sessionStorage hb_gift_code), else the line's own (the cart attribute _hb_gift_code). A new code brings the line back.
const GLINE_KEY = 'hb-gline-off';
const glineMem = new Set(); // when storage is blocked: for this page, then
function glineList() {
  let list = [];
  try {
    const v = JSON.parse(localStorage.getItem(GLINE_KEY) || '[]');
    if (Array.isArray(v)) list = v.map((c) => String(c).toUpperCase());
  } catch (e) {
    /* nothing kept */
  }
  glineMem.forEach((c) => {
    if (!list.includes(c)) list.push(c);
  });
  return list;
}
function glineCode() {
  let code = '';
  try {
    code = sessionStorage.getItem('hb_gift_code') || '';
  } catch (e) {
    /* none kept for the visit */
  }
  if (!code) code = document.querySelector('[data-hb-gline]')?.dataset.code || '';
  return String(code).trim().toUpperCase();
}
function glineSync() {
  const code = glineCode();
  const off = !!code && glineList().includes(code);
  const root = document.documentElement;
  if (root.classList.contains('hb-gline-off') !== off) root.classList.toggle('hb-gline-off', off);
}
glineSync();
// assets/gift-popup.js puts html.hb-gift-code-on once it has read her cart (and has kept her code for the visit by then)
new MutationObserver(glineSync).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
document.addEventListener('click', (e) => {
  const x = e.target instanceof Element ? e.target.closest('[data-hb-gline-x]') : null;
  if (!x) return;
  e.preventDefault();
  const code = glineCode() || 'GIFT';
  glineMem.add(code);
  const list = glineList();
  try {
    localStorage.setItem(GLINE_KEY, JSON.stringify(list.slice(-20)));
  } catch (err) {
    /* kept for this page only */
  }
  document.querySelectorAll('[data-hb-gline]').forEach((line) => line.classList.add('is-going'));
  setTimeout(() => {
    glineSync();
    document.querySelectorAll('[data-hb-gline]').forEach((line) => line.classList.remove('is-going'));
  }, 300);
});
